import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatLighthouseDate, openLighthouseBooking } from "../booking/lighthouse";
import StarRating from "../components/StarRating";
import { trackBookingButtonClick, trackBookingFormInteraction, trackBookingWidgetView } from "../tracking/ga.utils";

// Minimal styling for HTML5 date inputs to match existing design
const dateInputStyles = {
  colorScheme: 'light',
  fontSize: '15px',
  color: '#222',
  fontFamily: 'inherit',
} as React.CSSProperties;

// CSS to hide native date picker icon and use custom icon
const dateInputCSS = `
  .input1_inner input[type="date"]::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 20px;
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  .input1_inner input[type="date"]::-webkit-inner-spin-button {
    display: none;
  }

  .input1_inner input[type="date"]::-webkit-clear-button {
    display: none;
  }
`;


const ReservationSection: React.FC = () => {
  const { t: tCommon } = useTranslation("common");
  const { t: tContact } = useTranslation("contact");
  const { t: tHome, i18n } = useTranslation("home");

  // Get today's date in YYYY-MM-DD format for min attribute
  const today = new Date().toISOString().split('T')[0];

  // State for managing checkout min date
  const [checkoutMinDate, setCheckoutMinDate] = React.useState(today);


  // Track booking widget view when component mounts
  useEffect(() => {
    trackBookingWidgetView("reservation_section");
  }, []);

  const handleFormInteraction = (field: string) => {
    trackBookingFormInteraction(field);
  };

  const handleCheckinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;
    if (selectedDate) {
      // Set checkout min date to the day after check-in
      const checkinDate = new Date(selectedDate);
      checkinDate.setDate(checkinDate.getDate() + 1);
      const nextDay = checkinDate.toISOString().split('T')[0];
      setCheckoutMinDate(nextDay);
    } else {
      // Reset to today if no check-in date is selected
      setCheckoutMinDate(today);
    }
  };

  const handleBookingSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Get form data
    const formData = new FormData(e.currentTarget);
    const checkInValue = formData.get('checkin') as string;
    const checkOutValue = formData.get('checkout') as string;

    // Track booking button click
    trackBookingButtonClick({
      check_in: checkInValue,
      check_out: checkOutValue,
      room_type: 'reservation_form',
    });

    // Prepare booking parameters
    const bookingParams: any = {
      language: i18n.language || 'fr',
    };

    // Add dates if they are provided and valid
    if (checkInValue) {
      try {
        const checkInDate = new Date(checkInValue);
        if (!isNaN(checkInDate.getTime())) {
          bookingParams.checkIn = formatLighthouseDate(checkInDate);
        }
      } catch (error) {
        console.warn('Invalid check-in date:', checkInValue);
      }
    }

    if (checkOutValue) {
      try {
        const checkOutDate = new Date(checkOutValue);
        if (!isNaN(checkOutDate.getTime())) {
          bookingParams.checkOut = formatLighthouseDate(checkOutDate);
        }
      } catch (error) {
        console.warn('Invalid check-out date:', checkOutValue);
      }
    }

    // Open Lighthouse booking engine
    openLighthouseBooking(bookingParams);
  };
  
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: dateInputCSS }} />
      <section className="testimonials">
        <div
          className="background bg-img bg-fixed section-padding pb-0"
          data-background="images/slider/2.jpg"
          data-overlay-dark="4"
        >
          <div className="container">
            <div className="row">
              {/* Reservation */}
              <div className="col-md-5 mb-30 mt-30">
                <p>
                  <StarRating rating={"3"} />
                </p>
                <h5>
                  {tHome("reservation.description")}
                  <br />
                </h5>
                <h5>{tHome("reservation.callToAction")}</h5>
                <div className="reservations mb-30">
                  <div className="icon color-1">
                    <span className="flaticon-call"></span>
                  </div>
                  <div className="text">
                    <p className="color-1">{tHome("about.reservation")}</p>{" "}
                    <a
                      className="color-1"
                      href={`tel: ${tContact("phone.number")}`}
                    >
                      {tContact("phone.number")}
                    </a>
                  </div>
                </div>
                <p>
                  <i className="ti-check"></i>
                  <small>{tHome("reservation.freeCallText")}</small>
                </p>
              </div>

              {/* Booking From */}
              <div className="col-md-5 offset-md-2">
                <div className="booking-box">
                  <div className="head-box">
                    <h6>{tCommon("fullName")}</h6>
                    <h4>{tHome("reservation.formTitle")}</h4>
                  </div>
                  <div className="booking-inner clearfix">
                    <form onSubmit={handleBookingSubmit} className="form1 clearfix">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="input1_wrapper">
                            <label htmlFor="reservation-checkin">
                              {tHome("booking.checkIn.label")}
                            </label>
                            <div className="input1_inner">
                              <input
                                id="reservation-checkin"
                                name="checkin"
                                type="date"
                                className="form-control input"
                                style={dateInputStyles}
                                min={today}
                                aria-label={tHome("booking.checkIn.label")}
                                onClick={() => handleFormInteraction('checkin')}
                                onChange={handleCheckinChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="input1_wrapper">
                            <label htmlFor="reservation-checkout">
                              {tHome("booking.checkOut.label")}
                            </label>
                            <div className="input1_inner">
                              <input
                                id="reservation-checkout"
                                name="checkout"
                                type="date"
                                className="form-control input"
                                style={dateInputStyles}
                                min={checkoutMinDate}
                                aria-label={tHome("booking.checkOut.label")}
                                onClick={() => handleFormInteraction('checkout')}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <button
                            type="submit"
                            className="btn-form1-submit mt-15"
                          >
                            {tHome("booking.submitButton")}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReservationSection;
